/* eslint-disable @typescript-eslint/camelcase */

import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Status } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import useQueryParameters from 'hooks/useQueryParameters';
import useApplicationBasedOverrides from 'hooks/useApplicationBasedOverrides';

import {
  CreditApplication,
  mergeApplication,
  lockFields,
  selectCreditApplication,
} from 'state/creditApplication';
import { selectTenant } from 'state/tenant';
import { keys, pick, pickBy, isNil, complement } from 'ramda';
import useIovation from 'hooks/useIovation';
import isPresent from 'utils/isPresent';
import useAnalyticVariables from 'hooks/useAnalyticVariables';
import { ConsumerSearch, mergeConsumerSearch } from 'state/consumerSearch';
const PATH = '/api/unified_consumer_search';

export interface SearchParams {
  mobilePhone?: string;
  emailAddress?: string;
  storeNumber?: string;
  associateId?: string;
  otpCode?: string;
  carrier?: string;
  keyword?: string;
  shortCode?: string;
}

interface SearchResults {
  address1: string | undefined;
  address2: string | undefined;
  city: string | undefined;
  state: string | undefined;
  zip: string | undefined;
  mobilePhone: string | undefined;
  firsName: string | undefined;
  lastName: string | undefined;
  middleInitial: string | undefined;
}

type UnifiedConsumerSearchHook = () => [
  <T extends SearchParams = SearchParams>(params: T) => Promise<SearchResults>,
  Status,
  boolean,
];

export class UnifiedConsumerSearchError extends Error {}

const prefillValues = (results: SearchResults): Partial<CreditApplication> =>
  pickBy(complement(isNil), results);

/**
 * Copy existing values
 */
const overrideSearchResults = (
  searchResults: SearchResults,
  creditApplication: CreditApplication,
): SearchResults => {
  const searchResultsKeys = (keys(
    searchResults,
  ) as unknown) as (keyof SearchResults)[];

  const overrideValue = {
    ...creditApplication,
  };

  const overrides = pickBy(isPresent, overrideValue) as Partial<
    typeof overrideValue
  >;

  return pick(searchResultsKeys, {
    ...searchResults,
    ...overrides,
  }) as SearchResults;
};

const useUnifiedConsumerSearch: UnifiedConsumerSearchHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const dispatch = useDispatch();
  const { mockUCS, location, channel, subchannel } = useQueryParameters();
  const iovationBlackbox = useIovation();
  const creditApplication = useSelector(selectCreditApplication);
  const tenant = useSelector(selectTenant);
  const { setAnalyticsVariables } = useAnalyticVariables();
  const overridesAttributes = useApplicationBasedOverrides();

  const unifiedSearchConsumer = useCallback(
    async <T extends SearchParams = SearchParams>({
      carrier,
      keyword,
      mobilePhone,
      shortCode,
      emailAddress,
      storeNumber,
      associateId,
      otpCode,
    }: T): Promise<SearchResults> => {
      setError(false);

      const blackbox = await iovationBlackbox;

      const response = await fetch(PATH, {
        method: 'POST',
        body: {
          ...overridesAttributes,
          carrier: carrier,
          keyword: keyword,
          mobilePhone,
          shortCode,
          emailAddress,
          storeNumber,
          associateId,
          iovationBlackbox: blackbox,
          mockResponse: mockUCS,
          location,
          channel,
          subchannel,
          otpCode,
        },
      });

      if (!response.ok) {
        setError(true);
        dispatch(mergeConsumerSearch({ result: 'error' }));
        return Promise.reject(new UnifiedConsumerSearchError());
      }

      // TODO: Currently just shoving all of the data into place. Should probably
      // be more discerning somehow. Revisit this.
      let searchResults: SearchResults = await response.json();

      if (tenant.trustedBrandPrefill) {
        searchResults = overrideSearchResults(searchResults, creditApplication);
      }
      const unifiedConsumerSearchResult: ConsumerSearch = {
        result: 'success',
      };

      dispatch(mergeConsumerSearch(unifiedConsumerSearchResult));
      dispatch(mergeApplication(prefillValues(searchResults)));
      dispatch(
        lockFields({
          mobilePhone: Boolean(mobilePhone || searchResults.mobilePhone),
        }),
      );

      setAnalyticsVariables({ consumerSearchSuccessful: 'true' });
      return searchResults;
    },
    [
      iovationBlackbox,
      fetch,
      overridesAttributes,
      mockUCS,
      location,
      channel,
      subchannel,
      tenant.trustedBrandPrefill,
      dispatch,
      setAnalyticsVariables,
      creditApplication,
    ],
  );

  return [unifiedSearchConsumer, status, error];
};

export default useUnifiedConsumerSearch;
